"use client";

import { SafeHydrate } from "@/SafeHydrate";
import * as React from "react";
import { ArrowRight, Calendar } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AspectRatio } from "@/components/ui/aspect-ratio";
const formatDate = (dateString?: string) => {
  if (!dateString) return "";
  return new Date(dateString).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric"
  });
};
function LatestArticlesConsumer() {
  const {
    posts,
    loading,
    error,
    refetch
  } = usePosts();
  if (loading) {
    return <div data-section-id="13593" className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(3)].map((_, i) => <div key={i} data-index={i} className="flex flex-col gap-4 rounded-2xl border border-border bg-card/50 p-0 h-full overflow-hidden">
            <div className="w-full aspect-video bg-muted animate-pulse" />
            <div className="p-6 space-y-4">
              <div className="h-4 w-24 bg-muted animate-pulse rounded" />
              <div className="h-6 w-3/4 bg-muted animate-pulse rounded" />
              <div className="space-y-2">
                <div className="h-4 w-full bg-muted animate-pulse rounded" />
                <div className="h-4 w-2/3 bg-muted animate-pulse rounded" />
              </div>
            </div>
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="text-center py-12">
        <p className="text-muted-foreground mb-4">Failed to load articles.</p>
        <Button onClick={refetch} variant="outline" className="border-accent text-accent hover:bg-accent/10">
          Try Again
        </Button>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="text-center py-12 bg-muted/20 rounded-2xl border border-dashed border-border">
        <p className="text-muted-foreground">No articles found.</p>
      </div>;
  }
  return <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
      {posts.map((post, i) => <Link key={post.id} to={post.link || "#"} className="group block h-full">
          <Card data-index={i} className="h-full border border-white/20 bg-card/40 backdrop-blur-sm shadow-sm transition-all duration-500 hover:-translate-y-1 hover:shadow-xl rounded-2xl overflow-hidden flex flex-col">
            <div className="overflow-hidden">
              <AspectRatio ratio={16 / 9}>
                <img src={post.featuredImage || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1609030/img/blog_placeholder_generic.png"} alt={post.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110 sepia-[0.15] saturate-[1.1]" />
              </AspectRatio>
            </div>

            <CardHeader className="p-6 pb-2 space-y-3">
              <div className="flex items-center justify-between">
                {post.categories && post.categories.length > 0 && <Badge variant="secondary" className="bg-accent/10 text-accent hover:bg-accent/20 font-sans uppercase tracking-wider text-[10px] px-3 py-1 rounded-full">
                    {post.categories[0].name}
                  </Badge>}
                <div className="flex items-center text-[10px] uppercase tracking-widest font-sans text-muted-foreground">
                  <SafeHydrate><Calendar className="mr-1 h-3 w-3 text-accent" /></SafeHydrate>
                  {formatDate(post.date)}
                </div>
              </div>
              <h3 className="text-2xl font-serif font-semibold leading-tight text-foreground group-hover:text-accent transition-colors line-clamp-2">
                {post.title}
              </h3>
            </CardHeader>

            <CardContent className="p-6 pt-2 flex-grow">
              <div className="text-slate-600 font-sans text-base line-clamp-3 leading-relaxed" dangerouslySetInnerHTML={{
            __html: post.excerpt || ""
          }} />
            </CardContent>

            <CardFooter className="p-6 pt-0 mt-auto border-t-0">
              <div className="flex items-center text-accent font-sans font-medium text-sm group/btn">
                Read More
                <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-300 group-hover/btn:translate-x-[5px]" />
              </div>
            </CardFooter>
          </Card>
        </Link>)}
    </div>;
}
export default function LatestArticles() {
  const wp_query_params = {
    per_page: 3,
    orderby: "date",
    order: "desc"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="article-grid" className="py-20 bg-background text-foreground">
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex flex-col md:flex-row justify-between items-end mb-12 gap-6">
          <div className="max-w-2xl">
            <h2 className="text-3xl md:text-5xl font-serif font-bold tracking-tight text-foreground mb-4">
              Health Insights
            </h2>
            <p className="text-lg font-sans text-slate-600">
              Expert advice on spinal health, chiropractic care, and holistic wellness from our dedicated clinical team.
            </p>
          </div>
          <Link to="/HealthInsightsPage">
            <Button variant="outline" className="hidden md:flex border-accent text-accent hover:bg-accent hover:text-white rounded-full transition-all duration-300">
              View All Insights
            </Button>
          </Link>
        </div>

        <PostsProvider wp_query={{
        per_page: 3,
        orderby: "date",
        order: "desc"
      }}>
          <LatestArticlesConsumer />
        </PostsProvider>

        <div className="mt-10 text-center md:hidden">
          <Link to="/HealthInsightsPage">
            <Button variant="outline" className="w-full border-accent text-accent hover:bg-accent hover:text-white rounded-full">
              View All Insights
            </Button>
          </Link>
        </div>
      </div>
    </section>;
}